// ══════════════════════════════════════════════════════════════════════════════
//  fleetService.js — service-due math shared by Weekly Inventory and the Maintenance tab
//  #FLEET_SVC_STATE_V1 (2026-10-05)
//
//  Bill: "build out scheduled maintenance and flag units that are Overdue or hard to catch."
//  Bob's note: "due date by hours, miles, date (months) ex: big trucks 15,000 miles or 6 months.
//  Which ever comes 1st."
//
//  ONE place decides whether a unit is due, so the Weekly Inventory grid, the Fleet Manager tiles
//  and the Maintenance tab can never disagree.
//
//    rule      { interval, metric:'hrs'|'mi', months? }  — from FleetModule's serviceRuleFor()
//    serviced  reading at the last service (fleet-service)
//    dos       date of that service, MM/DD/YY or an old ISO value
//    reading   the LAST KNOWN reading for the unit — not only this week's. A rig that has not had a
//              reading typed this week is still past due if last week's reading was already over.
//
//  state     null       no schedule for this unit (trailers etc.)
//            'overdue'  at/past the due reading, or past the due date
//            'soon'     inside the last 10% of the interval, or inside 14 days of the due date
//            'ok'       scheduled, on file, not close
//            'unknown'  scheduled but something is missing — see `gap`
//  why       'reading' | 'date' | 'both'   what tripped overdue/soon
//  gap       'no-serviced' | 'no-reading' | 'reading-below-serviced' | 'far-over' | null
// ══════════════════════════════════════════════════════════════════════════════

export const SVC_SOON_FRACTION = 0.10;   // last 10% of the interval: 25 hrs · 600 mi · 1,500 mi
export const SVC_SOON_DAYS = 14;
export const SVC_TYPO_INTERVALS = 20;

const pad2 = (n) => String(n).padStart(2, '0');

// MM/DD/YY, MM/DD/YYYY or YYYY-MM-DD → a local Date at noon (noon so a DST edge never moves the day).
export const svcParseDate = (v) => {
  const s = String(v == null ? '' : v).trim();
  if (!s) return null;
  // A real calendar day only — 13/45/26 must not quietly roll over into some other month.
  const mk = (y, mo, da) => { const d = new Date(y, mo - 1, da, 12); return (isNaN(d) || d.getMonth() !== mo - 1 || d.getDate() !== da) ? null : d; };
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return mk(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (m) return mk(m[3].length === 2 ? (+m[3] < 70 ? 2000 : 1900) + +m[3] : +m[3], +m[1], +m[2]);
  return null;
};
// Always MM/DD/YY — Bill: "DOS Date to follow MM/DD/YY sequence."
export const svcFmtDate = (d) => (d instanceof Date && !isNaN(d))
  ? `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${String(d.getFullYear()).slice(2)}` : '';
export const svcTodayMDY = () => svcFmtDate(new Date());
// A stored DOS (either format) shown/saved as MM/DD/YY; anything unparseable is returned as typed so
// a half-typed date is not eaten while someone is still typing it.
export const svcNormDos = (v) => { const d = svcParseDate(v); return d ? svcFmtDate(d) : String(v == null ? '' : v).trim(); };

export const svcAddMonths = (d, n) => {
  const x = new Date(d.getFullYear(), d.getMonth() + n, 1, 12);
  const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  x.setDate(Math.min(d.getDate(), last));
  return x;
};
const dayDiff = (a, b) => Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000);

export function serviceState({ rule, serviced, dos, reading, today }) {
  const out = { state: null, why: null, gap: null, due: null, left: null, dueDate: null, daysLeft: null };
  if (!rule || !rule.interval) return out;
  const now = today || new Date();
  const sv = (serviced != null && serviced !== '' && !isNaN(Number(serviced))) ? Number(serviced) : null;
  const rd = (reading != null && reading !== '' && !isNaN(Number(reading))) ? Number(reading) : null;

  let byReading = null;
  if (sv != null) {
    out.due = Math.round((sv + rule.interval) * 100) / 100;
    if (rd != null) {
      out.left = Math.round((out.due - rd) * 100) / 100;
      if (rd < sv) out.gap = 'reading-below-serviced';   // meter swapped / typo — the math cannot be trusted
      // 20+ intervals past due (5,000 hrs on a rig, 300,000 mi on a semi) is a dropped digit in Serviced
      // or the reading, not a missed service — list it under "needs data" instead of crying wolf.
      else if (out.left <= -SVC_TYPO_INTERVALS * rule.interval) out.gap = 'far-over';
      else if (out.left <= 0) byReading = 'overdue';
      else if (out.left <= rule.interval * SVC_SOON_FRACTION) byReading = 'soon';
      else byReading = 'ok';
    } else out.gap = 'no-reading';
  } else out.gap = 'no-serviced';

  let byDate = null;
  const d = rule.months ? svcParseDate(dos) : null;
  if (d) {
    out.dueDate = svcAddMonths(d, rule.months);
    out.daysLeft = dayDiff(now, out.dueDate);
    byDate = out.daysLeft <= 0 ? 'overdue' : (out.daysLeft <= SVC_SOON_DAYS ? 'soon' : 'ok');
  }

  const rank = { overdue: 3, soon: 2, ok: 1 };
  const worst = Math.max(rank[byReading] || 0, rank[byDate] || 0);
  if (worst >= 2) {
    out.state = worst === 3 ? 'overdue' : 'soon';
    const r = (rank[byReading] || 0) === worst, dt = (rank[byDate] || 0) === worst;
    out.why = r && dt ? 'both' : (r ? 'reading' : 'date');
  } else if (byReading === 'ok') out.state = 'ok';
  else out.state = 'unknown';   // a date that is fine does not excuse a missing or untrustworthy reading
  return out;
}

// "56 hrs over" / "1,240 mi left" / "23 days over (6 mo)" — one short human line for a state.
export const svcSummary = (st, rule) => {
  if (!st || !st.state || !rule) return '';
  const u = rule.metric === 'mi' ? 'mi' : 'hrs';
  const parts = [];
  if (st.left != null && st.gap !== 'reading-below-serviced') {
    parts.push(st.left <= 0 ? `${Math.abs(Math.round(st.left)).toLocaleString()} ${u} over` : `${Math.round(st.left).toLocaleString()} ${u} left`);
  }
  if (st.daysLeft != null && (st.why === 'date' || st.why === 'both' || st.left == null)) {
    parts.push(st.daysLeft <= 0 ? `${Math.abs(st.daysLeft)} days past ${rule.months} mo` : `${st.daysLeft} days to ${rule.months} mo`);
  }
  if (st.gap === 'no-serviced') parts.push('no service on file');
  if (st.gap === 'no-reading') parts.push('no reading on file');
  if (st.gap === 'reading-below-serviced') parts.push('reading is below last service — check the numbers');
  if (st.gap === 'far-over') parts.push('too far over to be real — check Serviced and the reading');
  return parts.join(' · ');
};

// The same thing in as few characters as possible, for the Weekly Inventory grid's narrow Service Due
// cell: "56 over" · "20 left" · "6 mo +445d" · "6 mo in 10d".
export const svcShort = (st, rule) => {
  if (!st || !rule || (st.state !== 'overdue' && st.state !== 'soon')) return '';
  const parts = [];
  if ((st.why === 'reading' || st.why === 'both') && st.left != null) {
    parts.push(st.left <= 0 ? `${Math.abs(Math.round(st.left)).toLocaleString()} over` : `${Math.round(st.left).toLocaleString()} left`);
  }
  if ((st.why === 'date' || st.why === 'both') && st.daysLeft != null) {
    parts.push(st.daysLeft <= 0 ? `${rule.months} mo +${Math.abs(st.daysLeft)}d` : `${rule.months} mo in ${st.daysLeft}d`);
  }
  return parts.join(' · ');
};

// Mechanics — Bill, Sep 8: "Mechanic Drop Down names (ARIK, TERRY, KENNY)". Third-party shops are
// typed in (they change), so the list ends with the two write-in choices.
// #NICK_SWAP_V1 (2026-10-27) — display name swaps (Terry shows as Tracy to everyone but Terry). Delete the entry to swap back.
export const NICK_SWAP = { Terry: 'Tracy' };
export const SVC_MECHANICS = ['Arik', 'Terry', 'Kenny', 'Derrick'];   // #SVC_MECH_DERRICK_V1 (2026-10-22)

// fleet-service-log entry — the unit's service history ("completed shows what was done, mileage &
// date" — Bob). Appended every time a Serviced reading / DOS changes, from either screen.
export const svcLogEntry = ({ uk, unit, serviced, dos, prevServiced, prevDos, work, mechanic, shop, via, by }) => ({
  id: `${Date.now()}-${uk}`,
  uk, unit: unit || '',
  serviced: (serviced == null || serviced === '') ? null : Number(serviced),
  dos: svcNormDos(dos),
  prevServiced: (prevServiced == null || prevServiced === '') ? null : Number(prevServiced),
  prevDos: svcNormDos(prevDos),
  work: String(work || '').trim(), mechanic: String(mechanic || '').trim(), shop: String(shop || '').trim(),
  via: via || 'weekly', by: by || '', at: new Date().toISOString(),
});
